// Devil Fruit Directory — every Devil Fruit in the character data, filterable by type.

import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import animeConfig from '../config/animeConfig';
import characterProfiles from '../data/characterProfiles';
import './DevilFruitDirectory.css';

const isReal = (f) => f && !['none', 'n/a', ''].includes(f.toLowerCase());

const FRUITS = characterProfiles
  .filter((c) => isReal(c.devilFruit))
  .map((c) => ({
    fruit: c.devilFruit,
    type: c.devilFruitType || 'Unknown',
    user: c.name,
    slug: c.slug,
    image: c.image,
  }));

// Group filter buttons by broad type
const TYPE_FILTERS = ['All', 'Paramecia', 'Zoan', 'Logia'];
const matchesType = (fruitType, filter) =>
  filter === 'All' || fruitType.toLowerCase().includes(filter.toLowerCase());

const DevilFruitDirectory = () => {
  const navigate = useNavigate();
  const [filter, setFilter] = useState('All');
  const [query, setQuery] = useState('');

  const list = useMemo(() => {
    const q = query.trim().toLowerCase();
    return FRUITS.filter(
      (f) =>
        matchesType(f.type, filter) &&
        (!q || f.fruit.toLowerCase().includes(q) || f.user.toLowerCase().includes(q))
    );
  }, [filter, query]);

  // The same sea the rest of the archive sits on, under a dark wash so the
  // cards and the text keep their contrast.
  const seaStyle = {
    backgroundImage: `linear-gradient(to bottom, rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.86)), url(${animeConfig.onePiece.backgroundImage})`,
  };

  return (
    <>
      <Helmet>
        <title>Devil Fruit Directory — All One Piece Devil Fruits | One Piece Trivia</title>
        <meta name="description" content="Browse every One Piece Devil Fruit by type — Paramecia, Zoan, and Logia — and the characters who ate them." />
        <link rel="canonical" href="https://www.onepiecetrivia.com/fruits" />
        <meta property="og:title" content="Devil Fruit Directory" />
        <meta property="og:description" content="Every One Piece Devil Fruit by type — Paramecia, Zoan and Logia — and who ate them." />
        <meta property="og:url" content="https://www.onepiecetrivia.com/fruits" />
      </Helmet>

      <div className="df-container" style={seaStyle}>
        <h1 className="df-title">Devil Fruit Directory</h1>
        <p className="df-sub">Every Devil Fruit and its user. {FRUITS.length} fruits.</p>

        <input
          className="df-search"
          type="text"
          placeholder="Search a fruit or user..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />

        <div className="df-filters">
          {TYPE_FILTERS.map((t) => (
            <button
              key={t}
              className={`df-filter ${filter === t ? 'active' : ''}`}
              onClick={() => setFilter(t)}
            >
              {t}
            </button>
          ))}
        </div>

        <div className="df-grid">
          {list.map((f, i) => (
            <button
              key={f.slug || i}
              className="df-card"
              onClick={() => f.slug && navigate(`/characters/${f.slug}`)}
            >
              <img src={f.image} alt={f.user} className="df-img" loading="lazy" />
              <span className={`df-type df-type-${f.type.split(' ')[0].toLowerCase()}`}>{f.type}</span>
              <span className="df-fruit">{f.fruit}</span>
              <span className="df-user">{f.user}</span>
            </button>
          ))}
          {list.length === 0 && <p className="df-empty">No fruits match.</p>}
        </div>
      </div>
    </>
  );
};

export default DevilFruitDirectory;
